export interface ServiceFaq {
  q: string;
  a: string;
}

export interface Service {
  slug: string;
  /** Short name used in nav, cards, and breadcrumbs. */
  name: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: string;
  h1Accent: string;
  heroImage: string;
  intro: string[];
  includedTitle: string;
  included: string[];
  gallery: string[];
  faqs: ServiceFaq[];
}

export const SERVICES: Service[] = [
  {
    slug: 'general-contracting',
    name: 'General Contracting',
    metaTitle: 'Multifamily & Commercial General Contractor | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Licensed general contractor for multifamily and commercial properties in San Antonio and Austin — ground-up builds, project management, subcontractor coordination, and structural repairs. Call (210) 393-1759.',
    eyebrow: 'General Contracting · Project Management',
    h1: 'One contractor,',
    h1Accent: 'every trade.',
    heroImage: '/images/header.jpg',
    intro: [
      'Revive Builders is a licensed general contractor for multifamily and commercial properties across San Antonio, Austin, and Central Texas. Ground-up residential and multifamily construction, full project management, subcontractor coordination, and the scheduling, budgeting, and permitting that keep a project moving.',
      'Owners and property managers hire us to be the one accountable party. We build the budget, run the trades, pull the permits, and hand over finished work — one contract, one point of contact, one standard.',
    ],
    includedTitle: 'What we manage',
    included: [
      'Ground-up residential and multifamily construction',
      'Full project management and scheduling',
      'Subcontractor coordination under one contract',
      'Budgeting, estimating, and permitting',
      'Structural repairs: beam and post replacement, floor leveling',
      'Wood and metal framing',
    ],
    gallery: ['/images/aerial.jpg', '/images/planet-1.jpg', '/images/renata-1.jpg', '/images/roof-2.jpg'],
    faqs: [
      {
        q: 'What size projects do you take on?',
        a: 'From structural repairs on a single building to ground-up multifamily construction and full-property programs. If it needs a licensed, insured general contractor in Central Texas, it fits.',
      },
      {
        q: 'Do you handle permits and inspections?',
        a: 'Yes — permitting and inspection sequencing are part of our project management scope, so approvals never become the surprise on the critical path.',
      },
      {
        q: 'Can you take over a struggling project?',
        a: 'We regularly step into projects that stalled under another contractor. We assess what exists, re-scope honestly, and finish it.',
      },
    ],
  },
  {
    slug: 'multifamily-renovation',
    name: 'Renovations & Finish-Out',
    metaTitle: 'Multifamily Renovation & Interior Finish-Out Contractor | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Apartment renovations, unit turns and build-outs, and stage-1 interior finish-out — drywall, flooring, cabinets, trim — for multifamily properties in San Antonio and Austin. Request a bid: (210) 393-1759.',
    eyebrow: 'Renovations · Interior Finish-Out',
    h1: 'Multifamily renovation,',
    h1Accent: 'delivered whole.',
    heroImage: '/images/renata-1.jpg',
    intro: [
      'Value-add renovation is where Revive Builders lives. Full interior remodels, apartment unit turns and build-outs, and complete stage-1 interior finish-out — drywall hang, tape, float, and texture; LVP, tile, and carpet; cabinets, trim, doors, and hardware — across San Antonio and Austin multifamily properties.',
      'We phase work building-by-building so occupancy keeps producing, hold one finish standard across hundreds of units, and walk every space with your team before handoff.',
    ],
    includedTitle: 'Renovation scopes we run',
    included: [
      'Full interior remodels and unit renovation packages',
      'Apartment unit turns and build-outs',
      'Drywall: hang, tape, float, and texture',
      'LVP, tile, and carpet flooring',
      'Cabinets, trim, doors, and hardware',
      'Leasing office, clubhouse, and amenity transformations',
    ],
    gallery: ['/images/renata-2.jpg', '/images/renata-gym-1.jpg', '/images/renata-3.jpg', '/images/renata-gym-3.jpg'],
    faqs: [
      {
        q: 'Can you renovate while the property stays occupied?',
        a: 'Yes — most of our renovation work happens on occupied properties. We phase by building, coordinate access with your office, and keep punch lists tight.',
      },
      {
        q: 'Do you work from a unit-mix budget?',
        a: 'We price renovation packages by tier against your target rents, so you can decide unit-by-unit how deep to go and know the cost before keys are pulled.',
      },
      {
        q: 'What does "stage 1 finish-out" cover?',
        a: 'Everything that turns a shell or gutted space into a finished interior: drywall through texture, flooring, cabinets, doors, trim, and hardware — one crew, sequenced, no waiting between trades.',
      },
    ],
  },
  {
    slug: 'kitchen-bath-remodeling',
    name: 'Kitchens & Bathrooms',
    metaTitle: 'Apartment Kitchen & Bathroom Remodeling | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Turnkey kitchen and bathroom remodels for apartments and multifamily properties in San Antonio and Austin — cabinets, counters, tile, tubs, and fixtures, ready to lease. Call (210) 393-1759.',
    eyebrow: 'Turnkey Interiors',
    h1: 'Kitchen & bath remodels,',
    h1Accent: 'ready to lease.',
    heroImage: '/images/renata-kitchen-1.jpg',
    intro: [
      'Kitchens and bathrooms rent units. Revive Builders delivers turnkey kitchen and bath remodels across Central Texas multifamily properties — cabinets, stone, appliances, tile, tubs, and fixtures installed by one crew that owns the whole scope.',
      'Because we run every trade in-house sequence — demo, plumbing, electrical, tile, and finish — there is no waiting on the next subcontractor. A finished kitchen or bath in days, at a per-unit price you can roll across the property.',
    ],
    includedTitle: 'Turnkey scope, one crew',
    included: [
      'Cabinet replacement and refacing',
      'Granite and quartz countertops',
      'Appliance packages, delivered and installed',
      'Tile surrounds, backsplashes, and flooring',
      'Tub and shower replacement or resurfacing',
      'Vanities, fixtures, lighting, and hardware',
    ],
    gallery: ['/images/renata-kitchen-2.jpg', '/images/renata-br-2.jpg', '/images/renata-kitchen-3.jpg', '/images/renata-br-4.jpg'],
    faqs: [
      {
        q: 'Do you offer per-unit package pricing?',
        a: 'Yes. Kitchen and bath packages are priced per unit type, so a 1x1 and a 2x2 each have a known cost you can budget across the whole property.',
      },
      {
        q: 'How long does a kitchen turn take?',
        a: 'A typical apartment kitchen — cabinets, counters, appliances, and finish — comes back in under a week once materials are staged.',
      },
      {
        q: 'Can you match an existing renovation standard?',
        a: 'We regularly walk a model unit and match the finish spec exactly, so new turns are indistinguishable from the model.',
      },
    ],
  },
  {
    slug: 'apartment-make-ready',
    name: 'Make-Ready & Maintenance',
    metaTitle: 'Apartment Make-Ready & Maintenance Services | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Make-readies, punch lists, and preventative maintenance for property managers in San Antonio and Austin — minor electrical, plumbing, and drywall handled by one crew. Call (210) 393-1759.',
    eyebrow: 'Maintenance · Unit Turns',
    h1: 'Make-readys that keep',
    h1Accent: 'units leasing.',
    heroImage: '/images/renata-kitchen-2.jpg',
    intro: [
      'Every day a unit sits vacant is money off your NOI. Revive Builders runs make-readies, punch lists, and ongoing maintenance for property managers across San Antonio and Austin — one crew, every trade, on a schedule your leasing office can plan around.',
      'From a punch-out and paint to a full turn, we scope each unit against your budget tier, deliver a firm number, and hand back rent-ready keys. Preventative maintenance and minor electrical, plumbing, and drywall work keep the property off the emergency list.',
    ],
    includedTitle: 'Every turn, handled end-to-end',
    included: [
      'Make-readies and punch lists',
      'Preventative maintenance programs',
      'Minor electrical, plumbing, and drywall repairs',
      'Full interior paint and texture matching',
      'Flooring, fixture, and hardware swaps',
      'Rent-ready walk-through with your team',
    ],
    gallery: ['/images/renata-kitchen-1.jpg', '/images/renata-br-1.jpg', '/images/renata-kitchen-3.jpg', '/images/renata-br-3.jpg'],
    faqs: [
      {
        q: 'How fast can you turn a unit?',
        a: 'A standard make-ready typically runs 3–5 business days from key handoff. Heavier turns are scoped up front with a firm completion date.',
      },
      {
        q: 'Can you work across multiple properties in a portfolio?',
        a: 'That is how most of our clients use us. Consistent pricing across the portfolio, one point of contact, and unit-status reporting for regional managers.',
      },
      {
        q: 'Do you handle ongoing maintenance too?',
        a: 'Yes — preventative maintenance and minor electrical, plumbing, and drywall work are standing scopes, not just turn work.',
      },
    ],
  },
  {
    slug: 'exterior-construction',
    name: 'Exterior & Roofing',
    metaTitle: 'Exterior Construction & Repairs — Siding, Roofing, Concrete | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Exterior construction and repairs for multifamily and commercial properties in San Antonio and Austin: siding, roofing and waterproofing, window and door replacement, and concrete stairs, walks, and patios.',
    eyebrow: 'Exterior Construction · Repairs',
    h1: 'Exteriors built to',
    h1Accent: 'outlast Texas.',
    heroImage: '/images/roof-1.jpg',
    intro: [
      'Central Texas weather is hard on buildings. Revive Builders handles exterior construction and repairs across San Antonio and Austin — siding, roofing and waterproofing, window and door replacement, and the concrete stairs, walks, and patios that carry daily traffic.',
      'We inspect and document before we quote, coordinate with insurance scopes when storm damage is in play, and run work building-by-building so residents and parking are never surprised.',
    ],
    includedTitle: 'Exterior scopes we run',
    included: [
      'Siding repair and replacement',
      'Roofing and waterproofing',
      'Window and door replacement',
      'Concrete: stairs, walks, and patios',
      'Breezeway, fascia, and flashing repairs',
      'Storm damage scopes and insurance coordination',
    ],
    gallery: ['/images/roof-2.jpg', '/images/roof-3.jpg', '/images/roof-4.jpg', '/images/renata-ext-4.jpg'],
    faqs: [
      {
        q: 'Do you handle insurance claims for storm damage?',
        a: 'We document damage, prepare the scope, and work directly with your adjuster so the claim reflects what the building actually needs — then build to that scope.',
      },
      {
        q: 'Can you phase exterior work across a property?',
        a: 'Yes — we sequence buildings to keep parking and access open and hand over per-building completion reports.',
      },
      {
        q: 'Do you replace concrete flatwork?',
        a: 'Stairs, sidewalks, patios, and the trip hazards your insurance carrier flags — sawcut, removed, and repoured to spec.',
      },
    ],
  },
  {
    slug: 'demolition-haul-off',
    name: 'Demolition & Haul-Off',
    metaTitle: 'Demolition & Haul-Off Contractor | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Selective and full-gut demolition, concrete and exterior demo, and debris haul-off for multifamily and commercial properties in San Antonio and Austin. Same-week scheduling: (210) 393-1759.',
    eyebrow: 'Demolition · Haul-Off',
    h1: 'Cleared, hauled,',
    h1Accent: 'ready to build.',
    heroImage: '/images/aerial.jpg',
    intro: [
      'Every renovation starts with what comes out. Revive Builders runs selective and full-gut demolition, concrete and exterior demo, and debris haul-off across San Antonio and Austin — permitted, contained, and hauled on same-week scheduling.',
      'Because demo and rebuild live under one roof here, there is no gap between "cleared" and "under construction." The same contractor that guts the space starts the finish-out.',
    ],
    includedTitle: 'Demo scopes we run',
    included: [
      'Selective demolition and full gut to studs',
      'Concrete and exterior demolition',
      'Debris haul-off and disposal',
      'Move-out and eviction trash-outs',
      'Pre-renovation building cleanouts',
      'Dumpster logistics and site containment',
    ],
    gallery: ['/images/renata-ext-2.jpg', '/images/renata-ext-5.jpg', '/images/renata-ext-6.jpg', '/images/header.jpg'],
    faqs: [
      {
        q: 'How fast can you clear a unit or space?',
        a: 'Most single-unit trash-outs and selective demo are scheduled inside the same week. Full guts are scoped with a firm date up front.',
      },
      {
        q: 'Is disposal included in the price?',
        a: 'Yes — haul-off, dump fees, and disposal are built into the quoted number. No surprise dumpster invoices.',
      },
      {
        q: 'Can demo roll straight into the rebuild?',
        a: 'That is the point of one vendor: the crew that clears the space starts the finish-out, so there is no dead time between phases.',
      },
    ],
  },
  {
    slug: 'commercial-construction',
    name: 'Commercial Build-Outs',
    metaTitle: 'Commercial Build-Outs & Tenant Improvements | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Commercial construction and tenant build-outs in Austin and San Antonio — gyms, retail, leasing offices, and ground-up interiors, including Planet Fitness Austin. Request a bid: (210) 393-1759.',
    eyebrow: 'Commercial Construction',
    h1: 'Commercial build-outs,',
    h1Accent: 'built to open.',
    heroImage: '/images/planet-1.jpg',
    intro: [
      'When a commercial space has an opening date, the contractor is the schedule. Revive Builders delivers commercial build-outs and tenant improvements across Austin and San Antonio — from ground-up interior construction of a full-scale Planet Fitness facility in Austin to leasing offices and retail spaces.',
      'We manage the full interior scope: demolition, framing, MEP coordination, finishes, and fixture installation, with inspections sequenced so the certificate of occupancy lands when the lease says it should.',
    ],
    includedTitle: 'Build-out scopes we deliver',
    included: [
      'Ground-up commercial interior construction',
      'Tenant improvement and white-box build-outs',
      'Fitness facilities, retail, and office space',
      'Demolition, framing, drywall, and finishes',
      'Steel fabrication: stairs and rails',
      'Permit and inspection coordination',
    ],
    gallery: ['/images/planet-2.jpg', '/images/planet-3.jpg', '/images/renata-1.jpg', '/images/renata-2.jpg'],
    faqs: [
      {
        q: 'What size commercial projects do you take on?',
        a: 'From a leasing office refresh to a full-scale gym build-out like Planet Fitness Austin. If it is interior commercial construction in Central Texas, it is in our lane.',
      },
      {
        q: 'Do you handle permits and inspections?',
        a: 'Yes — permitting and inspection sequencing are on the critical path from day one, not a surprise at the end.',
      },
      {
        q: 'Can you work inside an operating property?',
        a: 'Most of our commercial work happens on live properties. We isolate work zones and schedule loud scopes around business hours.',
      },
    ],
  },
  {
    slug: 'restoration',
    name: 'Storm, Fire & Water Restoration',
    metaTitle: 'Storm, Fire & Water Damage Restoration | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Commercial and multifamily restoration in San Antonio and Austin — storm, fire, and water damage, concrete and roof seal coatings, insurance scope coordination, and full rebuild by one contractor.',
    eyebrow: 'Specialty Services · Restoration',
    h1: 'From damage to',
    h1Accent: 'done.',
    heroImage: '/images/noir-dusk.jpg',
    intro: [
      'When a property takes storm, fire, or water damage, the clock starts — on habitability, on insurance, on revenue. Revive Builders handles restoration for multifamily and commercial properties across San Antonio and Austin: assessment, insurance scope coordination, and the full rebuild under one contractor.',
      'Specialty scopes live here too: concrete and roof seal coatings that extend asset life, and in-house steel fabrication for stairs and rails.',
    ],
    includedTitle: 'Restoration scopes we run',
    included: [
      'Storm, fire, and water damage restoration',
      'Insurance scope documentation and coordination',
      'Concrete and roof seal coatings',
      'Steel fabrication — stairs and rails',
      'Structural drying-in and emergency stabilization',
      'Full rebuild through finish-out',
    ],
    gallery: ['/images/roof-3.jpg', '/images/renata-br-5.jpg', '/images/roof-4.jpg', '/images/renata-ext-3.jpg'],
    faqs: [
      {
        q: 'Do you work directly with insurance adjusters?',
        a: 'Yes. We document the damage, prepare the scope, and meet the adjuster on site so the claim reflects what the rebuild actually requires.',
      },
      {
        q: 'Can you stabilize a property fast after a storm?',
        a: 'Emergency dry-in and stabilization come first — then the documented scope and rebuild. Call (210) 393-1759 and we will get a crew moving.',
      },
      {
        q: 'What are seal coatings for?',
        a: 'Concrete and roof seal coatings extend the life of aging surfaces at a fraction of replacement cost — a budget-friendly line item for older properties.',
      },
    ],
  },
  {
    slug: 'cleaning-services',
    name: 'Cleaning Services',
    metaTitle: 'Construction & Multifamily Cleaning Services | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Post-construction final cleans, make-ready and common-area cleaning, and pressure washing for multifamily and commercial properties in San Antonio and Austin. Call (210) 393-1759.',
    eyebrow: 'Cleaning · Construction & Multifamily',
    h1: 'Clean enough to',
    h1Accent: 'hand over keys.',
    heroImage: '/images/renata-2.jpg',
    intro: [
      'A project is not done until it is clean, and a unit is not rentable until it shines. Revive Builders runs construction and multifamily cleaning across San Antonio and Austin — post-construction final cleans, make-ready and common-area cleaning, and pressure washing that resets a property’s curb appeal.',
      'For properties we already renovate or maintain, cleaning folds into the same PO — one vendor from demo to move-in-ready.',
    ],
    includedTitle: 'Cleaning scopes we run',
    included: [
      'Post-construction final cleans',
      'Make-ready and unit-turn cleaning',
      'Common-area and amenity cleaning',
      'Pressure washing: buildings, breezeways, and flatwork',
      'Trash-out and haul-away',
      'Move-in-ready detail and walk-through',
    ],
    gallery: ['/images/renata-gym-2.jpg', '/images/renata-kitchen-1.jpg', '/images/renata-br-2.jpg', '/images/renata-ext-1.jpg'],
    faqs: [
      {
        q: 'Do you do recurring common-area cleaning?',
        a: 'Yes — recurring common-area and amenity cleaning schedules are available for multifamily properties, alongside one-time post-construction and make-ready cleans.',
      },
      {
        q: 'Is pressure washing priced per building?',
        a: 'We quote by property walk: buildings, breezeways, and flatwork measured up front, one number for the whole scope.',
      },
    ],
  },
  {
    slug: 'landscaping-grounds',
    name: 'Lawn & Landscape',
    metaTitle: 'Commercial Landscaping & Grounds Care | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Routine grounds care for multifamily and commercial properties in San Antonio and Austin — mowing, mulch and planting, seasonal cleanups, and irrigation repairs and adjustments. Call (210) 393-1759.',
    eyebrow: 'Lawn & Landscape · Irrigation',
    h1: 'Grounds that close',
    h1Accent: 'leases.',
    heroImage: '/images/renata-ext-1.jpg',
    intro: [
      'A prospect decides in the parking lot. Revive Builders keeps multifamily and commercial grounds sharp across San Antonio and Austin — routine mowing, edging, and trimming; mulch and plant installation; and the seasonal cleanups and irrigation work that keep a property photographing well year-round.',
      'Routine property care runs on a schedule, not a phone call: a set cadence, one monthly invoice, and a crew that already knows your property.',
    ],
    includedTitle: 'Grounds scopes we run',
    included: [
      'Mowing, edging, and trimming on a set cadence',
      'Mulch and plant installation',
      'Shrub and ornamental trimming',
      'Seasonal property cleanups',
      'Irrigation repairs and adjustments',
      'Leaf, storm, and debris removal',
    ],
    gallery: ['/images/renata-ext-2.jpg', '/images/renata-ext-4.jpg', '/images/renata-ext-5.jpg', '/images/renata-ext-7.jpg'],
    faqs: [
      {
        q: 'Do you offer recurring maintenance contracts?',
        a: 'Yes — routine property care is a core service line. Weekly or bi-weekly cadence, a set monthly number, and seasonal work planned into the calendar.',
      },
      {
        q: 'Can you fix an irrigation system another company installed?',
        a: 'Yes. Repairs, head adjustments, seasonal startup and shutdown, and freeze-damage fixes — regardless of who installed it.',
      },
      {
        q: 'Can grounds care bundle with other services?',
        a: 'Most clients bundle it with make-readies or maintenance: one vendor, fewer POs, and a crew already on the property.',
      },
    ],
  },
  {
    slug: 'tree-services',
    name: 'Tree Services',
    metaTitle: 'Commercial Tree Service — Removal, Trimming & Storm Cleanup | San Antonio & Austin | Revive Builders',
    metaDescription:
      'Tree removal, trimming and pruning, stump grinding, hazard assessments, and storm cleanup for multifamily and commercial properties in San Antonio and Austin. Call (210) 393-1759.',
    eyebrow: 'Tree Services · Removal & Care',
    h1: 'Trees handled before',
    h1Accent: 'they become claims.',
    heroImage: '/images/renata-ext-6.jpg',
    intro: [
      'On a commercial property, a tree is either an asset or a liability. Revive Builders handles tree removal, trimming and pruning, stump grinding, and hazard assessments across San Antonio and Austin — and when a storm drops limbs across your property, we are the cleanup call.',
      'Hazard assessments catch the widow-makers before they hit a car, a roof, or a resident. Insurance carriers increasingly ask for exactly this documentation.',
    ],
    includedTitle: 'Tree scopes we run',
    included: [
      'Tree removal, including tight-access takedowns',
      'Trimming and structural pruning',
      'Stump grinding',
      'Hazard tree assessment and documentation',
      'Storm cleanup and emergency limb removal',
      'Haul-off and disposal included',
    ],
    gallery: ['/images/renata-ext-7.jpg', '/images/renata-ext-3.jpg', '/images/aerial.jpg', '/images/renata-ext-1.jpg'],
    faqs: [
      {
        q: 'Do you respond after storms?',
        a: 'Yes — storm cleanup and emergency limb removal are priority scopes. Call (210) 393-1759 and we will get a crew to the property.',
      },
      {
        q: 'Can you assess every tree on a property?',
        a: 'We walk the property, document hazard trees with photos, and give you a prioritized plan — useful for both budgeting and insurance.',
      },
      {
        q: 'Is haul-off included?',
        a: 'Always. Limbs, trunks, and grindings leave with us; the property is clean when we pull out.',
      },
    ],
  },
];

export const serviceBySlug = (slug: string) => SERVICES.find((s) => s.slug === slug);
